import { env } from './env';

declare global {
  interface Window {
    Gleap?: GleapClient;
    GleapActions?: Array<{ e: string; a: unknown[] }>;
  }
}

type GleapClient = {
  invoked: boolean;
} & Record<string, (...args: unknown[]) => void>;

const GLEAP_SCRIPT_ID = 'gleap-script';
/** Property only the pre-load buffering Proxy answers truthy (see `initGleap`). */
const GLEAP_PROXY_MARKER = '__trulyinboxGleapProxy';
const GLEAP_SCRIPT_SRC = 'https://sdk.gleap.io/latest/index.js';

/**
 * Initializes the Gleap support-chat widget once, before the app renders.
 * No-ops when no API key is configured so local dev without
 * `VITE_GLEAP_API_KEY` doesn't error or load the third-party script.
 *
 * Ported from trulyinbox-webui's `initializeGleap`: rather than adding
 * Gleap's npm SDK as a dependency, this queues calls onto a `window.Gleap`
 * Proxy (buffered into `window.GleapActions`) until the real script — loaded
 * async below — replaces it with the live SDK and flushes the buffer. This
 * is the SDK's own documented bootstrap shape, not a repo invention.
 */
export function initGleap(): void {
  if (!env.gleapApiKey) return;
  if (document.getElementById(GLEAP_SCRIPT_ID) || window.Gleap?.invoked) return;

  window.GleapActions = [];
  const proxy = new Proxy({ invoked: true } as GleapClient, {
    get(target, prop: string) {
      if (prop === 'invoked') return target.invoked;
      // Lets `whenGleapIdentified` tell this buffer from the real SDK.
      if (prop === GLEAP_PROXY_MARKER) return true;
      return (...args: unknown[]) => {
        window.GleapActions?.push({ e: prop, a: args });
      };
    },
    set(target, prop, value) {
      (target as Record<string, unknown>)[prop as string] = value;
      return true;
    },
  });
  window.Gleap = proxy;

  const script = document.createElement('script');
  script.id = GLEAP_SCRIPT_ID;
  script.async = true;
  script.src = GLEAP_SCRIPT_SRC;
  document.head.appendChild(script);

  window.Gleap.initialize(env.gleapApiKey);
  // No floating launcher inside the app: the sidebar's Help button opens the
  // widget (`openGleapChat`), and `styles/global.css` docks the panel beside
  // the sidebar. Hidden from the start so an in-app load never flashes it;
  // `AppShell` shows it again on the pages that have no sidebar (`/login`,
  // `/register`, `/auth/*`). Done in code, not via the Gleap dashboard's button setting,
  // because the legacy app shares this Gleap project and keeps its launcher.
  window.Gleap.showFeedbackButton(false);
}

/**
 * Shows or hides Gleap's floating launcher. `AppShell` shows it on the pages
 * without the sidebar, which have no Help button, and hides it everywhere else.
 */
export function setGleapLauncherVisible(visible: boolean): void {
  try {
    window.Gleap?.showFeedbackButton(visible);
  } catch (error) {
    console.error('[gleap] Failed to toggle the launcher:', error);
  }
}

/** Gap between the anchor's column and the docked panel, in px. */
const GLEAP_ANCHOR_GAP = 12;

/**
 * Opens the Gleap chat widget — the sidebar's Help button, and the "Chat with
 * support" action on the mailbox-connection-failure help prompt
 * (`NeedHelpConnectingBanner`). No-ops (with a console warning) if the widget
 * hasn't loaded, e.g. no API key configured or the third-party script hasn't
 * finished loading yet.
 *
 * With an `anchor`, the panel docks beside it: its bottom edge lines up with
 * the anchor's, and it starts just past the anchor's sidebar (or the anchor
 * itself outside one). The position is measured at click time and handed to
 * `styles/global.css` as `--gleap-left` / `--gleap-bottom`, so it follows a
 * collapsed or expanded sidebar. Without one, the vars are cleared and the
 * CSS falls back to docking at the bottom beside the sidebar.
 */
export function openGleapChat(anchor?: Element): void {
  const root = document.documentElement.style;
  if (anchor) {
    const rect = anchor.getBoundingClientRect();
    const column = (anchor.closest('.sidebar') ?? anchor).getBoundingClientRect();
    root.setProperty('--gleap-left', `${Math.round(column.right + GLEAP_ANCHOR_GAP)}px`);
    root.setProperty('--gleap-bottom', `${Math.max(0, Math.round(window.innerHeight - rect.bottom))}px`);
  } else {
    root.removeProperty('--gleap-left');
    root.removeProperty('--gleap-bottom');
  }
  try {
    window.Gleap?.open();
  } catch (error) {
    console.error('[gleap] Failed to open chat widget:', error);
  }
}

/**
 * Which frontend the session is on, so support doesn't have to ask: `v2` is
 * this Vite app, `v1` the legacy `trulyinbox-webui`. Not `package.json`'s
 * version — that tracks releases, a different question.
 *
 * MUST be sent inside `customData`, never as a top-level key on the identify
 * payload. Gleap's `identify()` accepts a CLOSED set of top-level properties
 * (`name`, `email`, `phone`, `value`, `plan`, `company`, `createdAt`,
 * `customData`) and silently discards anything else — no error, no warning,
 * the call still succeeds. That's why this field was invisible on tickets
 * while `name`/`email`/`phone` beside it showed fine: those three are on the
 * allow-list and `version` never was. Arbitrary attributes belong in
 * `customData`, which takes up to 35 string/number/boolean keys.
 *
 * `userId` below is the one top-level key outside that published list that is
 * confirmed to render on real tickets — leave it exactly where it is. (Gleap's
 * own docs disagree on whether it's a recognized property or whether the id
 * shown comes from identify()'s positional first argument; either way it
 * works today, and `gleap.test.ts` guards it against a well-meaning cleanup.)
 * https://docs.gleap.io/documentation/javascript/user-identity
 */
const GLEAP_APP_VERSION = 'v2';

export type GleapUserIdentity = {
  id: number;
  email: string;
  name: string | null;
};

/**
 * What support needs on a v2 ticket besides the identity: the plan, and one-click
 * links to the org in Apus and to the customer in Paddle — the same three fields
 * the legacy app sent (`trulyinbox-webui`'s `useGleapUserIdentityWithConfig`),
 * repointed at v2. Each may still be resolving when the first identify runs;
 * `GleapIdentitySync` re-identifies when it lands.
 */
export type GleapUserDetails = {
  /** E.164, from `GET /profile`. */
  phone?: string | null;
  /** The org's current plan name, from `GET /billing/subscription`. */
  plan?: string | null;
  /** The v2 organization id — keys the Apus link. */
  organizationId?: number | null;
  /** Paddle customer id (`ctm_…`); null for an org Paddle doesn't bill. */
  paddleCustomerId?: string | null;
};

/**
 * Apus's v2 org page — opens THIS org, where support can also "login as". The
 * legacy link searched the v1 users list (`/trulyinbox-users?search=<email>`);
 * the v2 list takes no search param, but every org has its own page.
 */
const APUS_TRULYINBOX_V2_ORG_URL = 'https://apus.saleshandy.com/trulyinbox-v2';

/** Paddle's customer page, per Paddle environment. */
const PADDLE_CUSTOMER_URL = {
  production: 'https://vendors.paddle.com/customers-v2',
  sandbox: 'https://sandbox-vendors.paddle.com/customers-v2',
} as const satisfies Record<typeof env.paddleEnvironment, string>;

export function apusOrganizationLink(organizationId: number | null | undefined): string | null {
  return organizationId ? `${APUS_TRULYINBOX_V2_ORG_URL}/${organizationId}` : null;
}

export function paddleCustomerLink(paddleCustomerId: string | null | undefined): string | null {
  return paddleCustomerId
    ? `${PADDLE_CUSTOMER_URL[env.paddleEnvironment]}/${encodeURIComponent(paddleCustomerId)}`
    : null;
}

/**
 * Identifies the current user to Gleap so support conversations/tickets are
 * scoped to *them* rather than to whatever identity the widget last held.
 *
 * `hash` is Gleap's Identity Verification signature —
 * `HMAC-SHA256(userId, GLEAP_USER_HASH_SECRET_KEY)`, computed server-side by
 * `GET /auth/gleap-user-hash` (`GleapIdentityService`) — required because
 * this Gleap project's dashboard has Secure Mode on; an unsigned `identify()`
 * call is rejected with a 401 from `api.gleap.io/sessions/identify` and
 * silently never takes effect; TRU-1141.
 *
 * The hash signs the userId ALONE, so custom-data fields can be added here
 * without touching `GleapIdentityService`.
 */
export function identifyGleapUser(
  user: GleapUserIdentity,
  hash: string,
  /** Optional: each resolves separately (the auth store holds none of them),
   * so any may not have landed yet. */
  details: GleapUserDetails = {},
): void {
  const userId = String(user.id);
  const contact = contactFields(details);
  const generation = ++identifyGeneration;
  try {
    const identified = window.Gleap?.identify(
      userId,
      {
        name: user.name || '--',
        email: user.email,
        userId,
        // `name`/`email` always get a placeholder — Gleap always shows those two.
        ...contact,
      },
      hash,
    );
    // `identify()` only SETS these fields when it CREATES the contact: on a
    // contact that already exists Gleap answers with the stored values and
    // keeps them (2026-11-03: a v2 identify carrying the new Apus link came
    // back with the legacy one, and the contact's `updatedAt` never moved).
    // `updateContact()` — Gleap's documented way to change an existing
    // contact — does overwrite, so the same fields are sent again through it
    // once the identify has landed.
    whenGleapIdentified(identified, userId, generation, () => {
      window.Gleap?.updateContact?.(contact);
    });
  } catch (error) {
    console.error('[gleap] Failed to identify user:', error);
  }
}

/**
 * The optional profile fields, shared by `identify` and `updateContact`. Every
 * one is OMITTED when empty, never sent as '' or '--': Gleap renders custom
 * data verbatim, so a placeholder reads as a stored value, and the legacy
 * app's always-empty `paddleCustomerLink: ''` is exactly the noise this avoids.
 */
function contactFields(details: GleapUserDetails): Record<string, unknown> {
  const apusProfileLink = apusOrganizationLink(details.organizationId);
  const paddleLink = paddleCustomerLink(details.paddleCustomerId);
  return {
    ...(details.phone ? { phone: details.phone } : {}),
    // `plan` is one of Gleap's top-level profile fields (see
    // `GLEAP_APP_VERSION`), shown as the contact's Plan.
    ...(details.plan ? { plan: details.plan } : {}),
    customData: {
      version: GLEAP_APP_VERSION,
      ...(apusProfileLink ? { apusProfileLink } : {}),
      ...(paddleLink ? { paddleCustomerLink: paddleLink } : {}),
    },
  };
}

/**
 * Bumped by every identify and every clear, so a follow-up still waiting on an
 * older identify (the user logged out, or another user signed in) never runs
 * against the identity that replaced it.
 */
let identifyGeneration = 0;

/** How long to wait for the real SDK + identity before giving up on the follow-up. */
const IDENTIFY_WAIT_MS = 30_000;
const IDENTIFY_POLL_MS = 500;

/**
 * Run `then` once THIS identify has landed — never before, or `updateContact`
 * would patch the anonymous session `clearIdentity()` just started.
 *
 * - Real SDK: `identify()` returns a promise; run on success, skip on failure.
 * - Before the SDK script has loaded, `window.Gleap` is the buffering Proxy and
 *   returns nothing; poll until the real SDK reports this user as identified.
 */
function whenGleapIdentified(
  identified: unknown,
  userId: string,
  generation: number,
  then: () => void,
): void {
  const run = () => {
    if (generation !== identifyGeneration) return;
    try {
      then();
    } catch (error) {
      console.error('[gleap] Failed to update contact:', error);
    }
  };

  if (identified && typeof (identified as Promise<unknown>).then === 'function') {
    (identified as Promise<unknown>).then(run, () => {});
    return;
  }

  const deadline = Date.now() + IDENTIFY_WAIT_MS;
  const timer = setInterval(() => {
    const gleap = window.Gleap as (GleapClient & { getIdentity?: () => { userId?: string } | undefined }) | undefined;
    const isRealSdk = Boolean(gleap) && !(gleap as Record<string, unknown>)[GLEAP_PROXY_MARKER];
    const identifiedAs = isRealSdk ? gleap?.getIdentity?.()?.userId : undefined;
    if (generation !== identifyGeneration || Date.now() > deadline) {
      clearInterval(timer);
    } else if (identifiedAs !== undefined && String(identifiedAs) === userId) {
      clearInterval(timer);
      run();
    }
  }, IDENTIFY_POLL_MS);
}

/**
 * Identifies an anonymous visitor — so an unauthenticated visit (login,
 * register, forgot password) never inherits ticket/conversation history left
 * behind by a previous session (including a previous *logged-in* session) on
 * the same browser. `anonymousUserId`/`anonymousUserHash` come from
 * `GET /auth/anonymous-user-hash`, minted fresh per call (never persisted),
 * so each page load gets its own ephemeral, unlinkable identity — and, like
 * `identifyGleapUser`, a real signed hash rather than an unsigned call.
 */
export function identifyAnonymousGleapVisitor(anonymousUserId: string, anonymousUserHash: string): void {
  identifyGeneration++;
  try {
    window.Gleap?.identify(
      anonymousUserId,
      {
        name: '--',
        email: '--',
        userId: anonymousUserId,
        // Sent here too — a login/register ticket is where "which app?" is
        // least answerable from the content.
        customData: { version: GLEAP_APP_VERSION },
      },
      anonymousUserHash,
    );
  } catch (error) {
    console.error('[gleap] Failed to identify anonymous visitor:', error);
  }
}

/**
 * Clears the current Gleap identity. Called before every identify (see
 * `GleapIdentitySync`) so a login, a logout, or one user replacing another
 * on a shared machine always flushes the previous identity's cached
 * conversation state rather than merely overwriting the display name on top
 * of it — this is the fix for tickets staying visible across that boundary.
 */
export function clearGleapIdentity(): void {
  identifyGeneration++;
  try {
    window.Gleap?.clearIdentity?.();
  } catch (error) {
    console.error('[gleap] Failed to clear identity:', error);
  }
}

/**
 * Opens support chat by clicking the sidebar's Help button, so it docks beside the
 * sidebar. Falls back to the default dock when the sidebar isn't mounted.
 */
export function openHelpChat(): void {
  const helpButton = document.querySelector<HTMLButtonElement>('[data-help-trigger]');
  if (helpButton) helpButton.click();
  else openGleapChat();
}
